import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Box,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import { exceedingResultsConstants } from '../../constants'

const styles = (theme: any) => ({
  dialogTitle: {
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  dialogContent: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '600px', // fixed height
  },
  tableWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  scrollContainer: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: '430px',
  },
  tableHeadCell: {
    backgroundColor: '#4682b4',
    color: 'white',
    fontWeight: 'bold',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    padding: 'unset',
  },
  checkbox: {
    transform: 'scale(0.8)',
    textAlign: 'center',
  },
  testName: {
    fontWeight: 'bold',
  },
  testValue: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  comments: {
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  textField: {
    fontSize: '0.875rem',
    whiteSpace: 'pre-wrap',
  },
  noResults: {
    padding: theme.spacing(2),
  },
  tableBodyCell: {
    '& .MuiTableRow-root': {
      padding: 'unset !important',
    },
  },
  paperCell: {
    maxHeight: '90vh',
  },
  dialogContainer: {
    padding: 'unset',
  },
})

const ExceedingResultsDialog = ({
  open,
  onClose,
  exceedingResults,
  onSubmit,
  mode,
}: {
  open: boolean
  onClose: () => void
  exceedingResults: any[]
  onSubmit?: (selectedResults: any[]) => void
  mode: 'submit' | 'approve' | 'view'
}) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [selectedResults, setSelectedResults] = useState(
    exceedingResults?.map((r) => ({
      ...r,
      user_consent: r?.user_consent || false,
      user_comments: r?.user_comments ?? '',
      validator_consent: r?.validator_consent || false,
      validator_comments: r?.validator_comments ?? '',
    }))
  )

  const handleCheckboxChange = (index: number) => {
    setSelectedResults((prev) =>
      prev?.map((result, i) =>
        i === index
          ? {
              ...result,
              ...(mode === exceedingResultsConstants.approve
                ? { validator_consent: !result.validator_consent }
                : { user_consent: !result.user_consent }),
            }
          : result
      )
    )
  }

  const handleNotesChange = (index: number, value: string) => {
    setSelectedResults((prev) =>
      prev?.map((result, i) =>
        i === index
          ? {
              ...result,
              ...(mode === exceedingResultsConstants.approve
                ? { validator_comments: value }
                : { user_comments: value }),
            }
          : result
      )
    )
  }

  const handleSelectAllChange = () => {
    const newValue = !allSelected
    setSelectedResults((prev) =>
      prev?.map((result) => ({
        ...result,
        ...(mode === exceedingResultsConstants?.approve
          ? { validator_consent: newValue }
          : { user_consent: newValue }),
      }))
    )
  }

  // Compute allSelected based on selectedResults instead of separate state
  const allSelected =
    selectedResults?.length > 0 &&
    selectedResults?.every((result) =>
      mode === exceedingResultsConstants?.approve
        ? result?.validator_consent
        : result?.user_consent
    )

  const handleSubmit = () => {
    const filtered = selectedResults?.filter((result) =>
      mode === exceedingResultsConstants?.approve
        ? result?.validator_consent && result?.validator_comments?.trim() !== ''
        : result?.user_consent && result?.user_comments?.trim() !== ''
    )
    onSubmit?.(filtered)
  }

  const isSubmitEnabled = selectedResults?.every((result) =>
    mode === exceedingResultsConstants?.approve
      ? result?.validator_consent && result?.validator_comments?.trim() !== ''
      : result?.user_consent && result?.user_comments?.trim() !== ''
  )
  useEffect(() => {
    setSelectedResults(
      exceedingResults?.map((r) => ({
        ...r,
        user_consent: r?.user_consent ?? false,
        user_comments: r?.user_comments ?? '',
        validator_consent: r?.validator_consent ?? false,
        validator_comments: r?.validator_comments ?? '',
      }))
    )
  }, [exceedingResults])
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='xl'
      PaperProps={{ className: classes?.paperCell }}
      className={classes?.dialogContainer}
    >
      <DialogTitle className={classes?.dialogTitle}>
        {mode === exceedingResultsConstants?.submit
          ? exceedingResultsConstants?.submitResults
          : exceedingResultsConstants?.approveResults}
        <IconButton onClick={onClose} className={classes?.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes?.dialogContent}>
        {exceedingResults?.length ? (
          <Box className={classes?.tableWrapper}>
            <Box className={classes?.scrollContainer}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {mode !== exceedingResultsConstants?.view && (
                      <TableCell
                        className={classes?.tableHeadCell}
                        align='center'
                      >
                        <Checkbox
                          checked={allSelected}
                          onChange={handleSelectAllChange}
                          indeterminate={
                            selectedResults?.some((result) =>
                              mode === exceedingResultsConstants?.approve
                                ? result?.validator_consent
                                : result?.user_consent
                            ) && !allSelected
                          }
                          className={classes?.checkbox}
                        />
                      </TableCell>
                    )}
                    <TableCell className={classes?.tableHeadCell}>
                      Parameter Name
                    </TableCell>
                    <TableCell className={classes?.tableHeadCell}>
                      Value
                    </TableCell>

                    {(mode === exceedingResultsConstants?.approve ||
                      mode === exceedingResultsConstants?.view) && (
                      <TableCell className={classes?.tableHeadCell}>
                        User Comments
                      </TableCell>
                    )}

                    <TableCell className={classes?.tableHeadCell}>
                      {mode === exceedingResultsConstants?.approve ||
                      mode === exceedingResultsConstants?.view
                        ? exceedingResultsConstants.validatorComments
                        : exceedingResultsConstants.userComments}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {exceedingResults?.map((result, index) => (
                    <TableRow
                      key={result?.id}
                      className={classes?.tableBodyCell}
                    >
                      {mode !== exceedingResultsConstants?.view && (
                        <TableCell align='center'>
                          <Checkbox
                            checked={
                              mode === exceedingResultsConstants?.approve
                                ? selectedResults[index]?.validator_consent ||
                                  false
                                : selectedResults[index]?.user_consent || false
                            }
                            onChange={() => handleCheckboxChange(index)}
                            className={classes.checkbox}
                          />
                        </TableCell>
                      )}

                      <TableCell>
                        <Typography className={classes.testName}>
                          {result?.lab_test?.name || '-'}
                          {result?.lab_test?.panel?.name
                            ? `-${result?.lab_test?.panel?.name}`
                            : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography className={classes.testValue}>
                          {result?.value || '-'}
                        </Typography>
                      </TableCell>

                      {(mode === exceedingResultsConstants?.approve ||
                        mode === exceedingResultsConstants?.view) && (
                        <TableCell>
                          <Typography className={classes?.comments}>
                            {result?.user_comments ||
                              exceedingResultsConstants?.NocommentsProvided}
                          </Typography>
                        </TableCell>
                      )}

                      <TableCell>
                        {mode === exceedingResultsConstants?.view ? (
                          <Typography className={classes.comments}>
                            {result?.validator_comments ||
                              exceedingResultsConstants?.NocommentsProvided}
                          </Typography>
                        ) : (
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size='small'
                            variant='outlined'
                            placeholder={
                              mode === exceedingResultsConstants?.approve
                                ? exceedingResultsConstants?.validatorComments
                                : exceedingResultsConstants?.userComments
                            }
                            value={
                              mode === exceedingResultsConstants?.approve
                                ? selectedResults?.[index]
                                    ?.validator_comments || ''
                                : selectedResults?.[index]?.user_comments || ''
                            }
                            onChange={(e) =>
                              handleNotesChange(index, e.target.value)
                            }
                            InputProps={{
                              className: classes?.textField,
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        ) : (
          <Typography className={classes?.noResults}>
            No results found.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color='secondary'>
          Cancel
        </Button>

        {mode !== exceedingResultsConstants.view && (
          <Button
            onClick={handleSubmit}
            variant='contained'
            color='primary'
            disabled={!isSubmitEnabled}
          >
            {mode === 'submit'
              ? exceedingResultsConstants.Submit
              : exceedingResultsConstants.Approve}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ExceedingResultsDialog
